import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import {
    financeText,
    getFinanceLanguage,
    useFinanceDocumentLang,
    saveFinanceLanguage,
} from "finance-language";

import { Alert, Card, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, InputAdornment, MenuItem, Snackbar, Stack, Tab, Tabs, Tooltip } from "@mui/material";
// Büyük formda her tuşta tüm formun yeniden çizilmesini önleyen tamponlu TextField (yazma gecikmesi)
import TextField from "components/BufferedTextField";

import {
    AddOutlined,
    CancelOutlined,
    CheckCircleOutline,
    CreditCardOutlined,
    DeleteOutlineOutlined,
    EditOutlined,
    PaymentsOutlined,
    PointOfSaleOutlined,
    RefreshOutlined,
    SearchOutlined,
    SyncAltOutlined,
    WarningAmberRounded,
} from "@mui/icons-material";

import {
    FinanceKpiTile,
} from "../crm/invoice/finance/shared/FinanceUiKit";
// Koyu hero bandı için (yedekten geri yüklendi)
import { StorefrontOutlined } from "@mui/icons-material";
import { AccountBalanceOutlined } from "@mui/icons-material";
import { CalendarMonth } from "@mui/icons-material";
/* =========================================================
   CONFIG
========================================================= */

const API_URL =
    process.env.REACT_APP_API_URL;

/*
 * Backend route:
 *
 * .use("/finance/cash", cashRoutes)
 *
 * Company Cash:
 * /api/finance/cash/...
 */
const CASH_API =
    "/finance/cash";

/* =========================================================
   CONSTANTS
========================================================= */

const CURRENCIES = [
    "TRY",
    "USD",
    "EUR",
    "GBP",
];

const POS_TYPES = [
    {
        value: "physical",
        label: "Physical POS",
    },
    {
        value: "virtual",
        label: "Virtual POS",
    },
];

const emptyPosForm = {
    name: "",
    bankName: "",
    bankDetailId: "",
    merchantNo: "",
    type: "physical",
    currency: "TRY",
    commissionRate: "",
    settlementDays: "1",
    settlementAccountId: "",
    description: "",
    isActive: true,
};

const emptyCollectionForm = {
    posAccountId: "",
    amount: "",
    customerId: "",
    referenceNo: "",
    description: "",
};

/* =========================================================
   COLORS
========================================================= */

const COLORS = {
    navy: "#0B1F3A",

    blue: "#175CD3",
    blueLight: "#EFF8FF",

    green: "#027A48",
    greenLight: "#ECFDF3",

    red: "#B42318",
    redLight: "#FEF3F2",

    amber: "#B54708",
    amberLight: "#FFFAEB",

    purple: "#6941C6",
    purpleLight: "#F4F3FF",

    text: "#101828",
    subtext: "#667085",

    border: "#E4E7EC",
    page: "#F5F7FA",
    white: "#FFFFFF",
};

const cardSx = {
    borderRadius: "10px",

    background:
    COLORS.white,

    border: "1px solid rgba(15, 23, 42, 0.12)",

    boxShadow: "none",

    overflow:
        "hidden",
};

/* =========================================================
   HELPERS
========================================================= */

const getId = (
    value
) => {
    if (!value) {
        return "";
    }

    if (
        typeof value ===
        "object"
    ) {
        return String(
            value._id ||
            value.id ||
            ""
        );
    }

    return String(
        value
    );
};

const normalizeText = (
    value
) =>
    String(
        value || ""
    ).trim();

/* =========================================================
   NORMALIZE LIST
========================================================= */

const normalizeList = (
    payload
) => {
    let items =
        [];

    if (
        Array.isArray(
            payload
        )
    ) {
        items =
            payload;
    } else if (
        Array.isArray(
            payload?.data
        )
    ) {
        items =
            payload.data;
    } else if (
        Array.isArray(
            payload?.data?.data
        )
    ) {
        items =
            payload.data.data;
    } else if (
        Array.isArray(
            payload?.results
        )
    ) {
        items =
            payload.results;
    } else if (
        Array.isArray(
            payload?.posAccounts
        )
    ) {
        items =
            payload.posAccounts;
    } else if (
        Array.isArray(
            payload?.collections
        )
    ) {
        items =
            payload.collections;
    } else if (
        Array.isArray(
            payload?.accounts
        )
    ) {
        items =
            payload.accounts;
    } else if (
        Array.isArray(
            payload?.bankDetails
        )
    ) {
        items =
            payload.bankDetails;
    }

    return items.map(
        (
            item
        ) => {
            if (
                item?.attributes
            ) {
                return {
                    _id:
                        item.id ||
                        item.attributes?._id,

                    ...item.attributes,
                };
            }

            return item;
        }
    );
};

/* =========================================================
   MONEY
========================================================= */

const formatMoney = (
    amount,
    currency = "TRY"
) => {
    try {
        return new Intl.NumberFormat(
            "tr-TR",
            {
                style:
                    "currency",

                currency,

                minimumFractionDigits:
                    2,

                maximumFractionDigits:
                    2,
            }
        ).format(
            Number(
                amount
            ) ||
            0
        );
    } catch {
        return `${
            Number(
                amount
            ) ||
            0
        } ${currency}`;
    }
};

/* =========================================================
   DATE
========================================================= */

const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return date.toLocaleString(
        "tr-TR"
    );
};

/* =========================================================
   STATUS
========================================================= */

const getStatusConfig = (
    status
) => {
    switch (
        status
        ) {
        case "settled":
            return {
                label: "Settled",

                color:
                COLORS.green,

                background:
                COLORS.greenLight,

                border:
                    "#ABEFC6",
            };

        case "cancelled":
            return {
                label: "Cancelled",

                color:
                COLORS.red,

                background:
                COLORS.redLight,

                border:
                    "#FECDCA",
            };

        default:
            return {
                label: "Pending",

                color:
                COLORS.amber,

                background:
                COLORS.amberLight,

                border:
                    "#FEDF89",
            };
    }
};

/* =========================================================
   ERROR
========================================================= */

const getErrorMessage = (
    error,
    fallback
) =>
    error?.message ||
    fallback ||
    financeText("An unexpected error occurred.");

/* =========================================================
   ACTION BUTTON
========================================================= */

const getActionButtonSx = (
    tone
) => {
    const configs = {
        collection: {
            color:
            COLORS.blue,

            background:
            COLORS.blueLight,

            border:
                "#B2DDFF",

            hover:
                "#D1E9FF",
        },

        settle: {
            color:
            COLORS.green,

            background:
            COLORS.greenLight,

            border:
                "#ABEFC6",

            hover:
                "#D1FADF",
        },

        cancel: {
            color:
            COLORS.amber,

            background:
            COLORS.amberLight,

            border:
                "#FEDF89",

            hover:
                "#FEF0C7",
        },

        edit: {
            color:
            COLORS.purple,

            background:
            COLORS.purpleLight,

            border:
                "#D9D6FE",

            hover:
                "#E9E7FD",
        },

        delete: {
            color:
            COLORS.red,

            background:
            COLORS.redLight,

            border:
                "#FECDCA",

            hover:
                "#FEE4E2",
        },
    };

    const config =
        configs[tone] ||
        configs.collection;

    return {
        width:
            36,

        height:
            36,

        color:
        config.color,

        background:
        config.background,

        border:
            `1px solid ${config.border}`,

        borderRadius:
            "9px",

        "&:hover": {
            background:
            config.hover,
        },

        "&.Mui-disabled": {
            color:
                "#98A2B3",

            background:
                "#F2F4F7",

            border:
                `1px solid ${COLORS.border}`,
        },
    };
};

/* =========================================================
   METRIC CARD
========================================================= */

// Görünüm Finance kitindeki KPI kartıyla aynı (bkz. shared/FinanceUiKit.js)
const MetricCard = (props) => <FinanceKpiTile {...props} />;

/* =========================================================
   MAIN COMPONENT
========================================================= */

const PosAccounts = () => {
    const [financeLanguage, setFinanceLanguageState] = useState(getFinanceLanguage);

    useFinanceDocumentLang(financeLanguage);

    const changeFinanceLanguage = (event) => {
        const nextLanguage = event.target.value === "tr" ? "tr" : "en";
        saveFinanceLanguage(nextLanguage);
        setFinanceLanguageState(nextLanguage);
    };

    const token =
        localStorage.getItem(
            "token"
        );

    /* =====================================================
       DATA
    ===================================================== */

    const [
        posAccounts,
        setPosAccounts,
    ] = useState([]);

    const [
        collections,
        setCollections,
    ] = useState([]);

    const [
        cashAccounts,
        setCashAccounts,
    ] = useState([]);

    const [
        bankDetails,
        setBankDetails,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        saving,
        setSaving,
    ] = useState(false);

    /* =====================================================
       UI
    ===================================================== */

    const [
        currentTab,
        setCurrentTab,
    ] = useState(0);

    const [
        summaryCurrency,
        setSummaryCurrency,
    ] = useState(
        "TRY"
    );

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        collectionSearch,
        setCollectionSearch,
    ] = useState("");

    const [
        collectionStatus,
        setCollectionStatus,
    ] = useState(
        "all"
    );

    const [
        lastUpdated,
        setLastUpdated,
    ] = useState(
        new Date()
    );

    /* =====================================================
       POS FORM
    ===================================================== */

    const [
        posModal,
        setPosModal,
    ] = useState(false);

    const [
        editingPos,
        setEditingPos,
    ] = useState(null);

    const [
        posForm,
        setPosForm,
    ] = useState(
        emptyPosForm
    );

    /* =====================================================
       {financeText("COLLECTION")}
    ===================================================== */

    const [
        collectionModal,
        setCollectionModal,
    ] = useState(false);

    const [
        collectionForm,
        setCollectionForm,
    ] = useState(
        emptyCollectionForm
    );

    /* =====================================================
       ACTION TARGETS
    ===================================================== */

    const [
        deleteTarget,
        setDeleteTarget,
    ] = useState(null);

    const [
        settleTarget,
        setSettleTarget,
    ] = useState(null);

    const [
        cancelTarget,
        setCancelTarget,
    ] = useState(null);

    /* =====================================================
       NOTIFICATION
    ===================================================== */

    const [
        notification,
        setNotification,
    ] = useState({
        open:
            false,

        message:
            "",

        severity:
            "success",
    });

    const showNotification =
        useCallback(
            (
                message,
                severity =
                    "success"
            ) => {
                setNotification({
                    open:
                        true,

                    message,

                    severity,
                });
            },

            []
        );

    const closeNotification =
        () => {
            setNotification(
                (
                    previous
                ) => ({
                    ...previous,

                    open:
                        false,
                })
            );
        };

    /* =====================================================
       API
    ===================================================== */

    const apiRequest =
        useCallback(
            async (
                path,
                options = {}
            ) => {
                const baseUrl =
                    String(
                        API_URL ||
                        ""
                    ).replace(
                        /\/+$/,
                        ""
                    );

                const headers = {
                    "Content-Type":
                        "application/json",

                    ...(options.headers ||
                        {}),
                };

                if (token) {
                    headers.Authorization =
                        `Bearer ${token}`;
                }

                const response =
                    await fetch(
                        `${baseUrl}${path}`,

                        {
                            ...options,

                            headers,
                        }
                    );

                let body =
                    null;

                try {
                    body =
                        await response.json();
                } catch {
                    body =
                        null;
                }

                if (
                    !response.ok
                ) {
                    throw new Error(
                        body
                            ?.errors?.[0]
                            ?.detail ||
                        body
                            ?.message ||
                        `Request failed with status ${response.status}.`
                    );
                }

                return body;
            },

            [
                token,
            ]
        );

    /* =====================================================
       FETCH DATA

       POS:
       /api/pos-accounts

       COLLECTION:
       /api/pos-accounts/collections/list

       CASH:
       /api/finance/cash/accounts
    ===================================================== */

    const fetchData =
        useCallback(
            async () => {
                setLoading(
                    true
                );

                try {
                    const results =
                        await Promise.allSettled(
                            [
                                apiRequest(
                                    "/pos-accounts"
                                ),

                                apiRequest(
                                    "/pos-accounts/collections/list"
                                ),

                                apiRequest(
                                    `${CASH_API}/accounts`
                                ),

                                apiRequest(
                                    "/bank-detail"
                                ),
                            ]
                        );

                    const [
                        posResult,
                        collectionResult,
                        cashResult,
                        bankResult,
                    ] =
                        results;

                    /* =====================================
                       POS
                    ===================================== */

                    if (
                        posResult.status ===
                        "fulfilled"
                    ) {
                        setPosAccounts(
                            normalizeList(
                                posResult.value
                            )
                        );
                    } else {
                        console.error(
                            "POS accounts error:",
                            posResult.reason
                        );

                        setPosAccounts(
                            []
                        );

                        showNotification(
                            financeText("POS accounts could not be loaded."),

                            "error"
                        );
                    }

                    /* =====================================
                       COLLECTIONS
                    ===================================== */

                    if (
                        collectionResult.status ===
                        "fulfilled"
                    ) {
                        setCollections(
                            normalizeList(
                                collectionResult.value
                            )
                        );
                    } else {
                        console.error(
                            "POS collections error:",
                            collectionResult.reason
                        );

                        setCollections(
                            []
                        );

                        showNotification(
                            financeText("POS collections could not be loaded."),

                            "error"
                        );
                    }

                    /* =====================================
                       CASH
                    ===================================== */

                    if (
                        cashResult.status ===
                        "fulfilled"
                    ) {
                        setCashAccounts(
                            normalizeList(
                                cashResult.value
                            )
                        );
                    } else {
                        console.error(
                            "Cash accounts error:",
                            cashResult.reason
                        );

                        setCashAccounts(
                            []
                        );

                        showNotification(
                            financeText("Company Cash accounts could not be loaded."),

                            "error"
                        );
                    }

                    /* =====================================
                       BANK
                    ===================================== */

                    if (
                        bankResult.status ===
                        "fulfilled"
                    ) {
                        setBankDetails(
                            normalizeList(
                                bankResult.value
                            )
                        );
                    } else {
                        console.error(
                            "Bank details error:",
                            bankResult.reason
                        );

                        setBankDetails(
                            []
                        );
                    }

                    setLastUpdated(
                        new Date()
                    );
                } catch (
                    error
                    ) {
                    console.error(
                        "POS fetch error:",
                        error
                    );

                    showNotification(
                        getErrorMessage(
                            error,

                            financeText("POS data could not be loaded.")
                        ),

                        "error"
                    );
                } finally {
                    setLoading(
                        false
                    );
                }
            },

            [
                apiRequest,
                showNotification,
            ]
        );

    useEffect(
        () => {
            fetchData();
        },

        [
            fetchData,
        ]
    );

    /* =====================================================
       RESOLVERS
    ===================================================== */

    const resolveCashAccount =
        useCallback(
            (
                value
            ) => {
                if (!value) {
                    return null;
                }

                if (
                    typeof value ===
                    "object"
                ) {
                    return value;
                }

                return (
                    cashAccounts.find(
                        (
                            account
                        ) =>
                            getId(
                                account
                            ) ===
                            String(
                                value
                            )
                    ) ||
                    null
                );
            },

            [
                cashAccounts,
            ]
        );

    const resolvePos =
        useCallback(
            (
                value
            ) => {
                if (!value) {
                    return null;
                }

                if (
                    typeof value ===
                    "object"
                ) {
                    return value;
                }

                return (
                    posAccounts.find(
                        (
                            pos
                        ) =>
                            getId(
                                pos
                            ) ===
                            String(
                                value
                            )
                    ) ||
                    null
                );
            },

            [
                posAccounts,
            ]
        );

    const resolveCollectionSettlementAccount =
        useCallback(
            (
                collection
            ) => {
                if (!collection) {
                    return null;
                }

                /*
                 * Collection üzerinde settlementAccountId
                 * varsa önce onu kullan.
                 */
                const directAccount =
                    resolveCashAccount(
                        collection.settlementAccountId
                    );

                if (
                    directAccount
                ) {
                    return directAccount;
                }

                /*
                 * Yoksa collection'ın POS'una git.
                 */
                const pos =
                    resolvePos(
                        collection.posAccountId
                    );

                if (!pos) {
                    return null;
                }

                return resolveCashAccount(
                    pos.settlementAccountId
                );
            },

            [
                resolveCashAccount,
                resolvePos,
            ]
        );

    /* =====================================================
       SUMMARY
    ===================================================== */

    const currencyPosAccounts =
        useMemo(
            () =>
                posAccounts.filter(
                    (
                        pos
                    ) =>
                        pos.currency ===
                        summaryCurrency
                ),

            [
                posAccounts,
                summaryCurrency,
            ]
        );

    const currencyCollections =
        useMemo(
            () =>
                collections.filter(
                    (
                        collection
                    ) => {
                        const pos =
                            resolvePos(
                                collection.posAccountId
                            );

                        const collectionCurrency =
                            collection.currency ||
                            pos?.currency;

                        return (
                            collectionCurrency ===
                            summaryCurrency
                        );
                    }
                ),

            [
                collections,
                summaryCurrency,
                resolvePos,
            ]
        );

    const summary =
        useMemo(
            () => {
                const pending =
                    currencyCollections
                        .filter(
                            (
                                item
                            ) =>
                                item.status ===
                                "pending"
                        )
                        .reduce(
                            (
                                total,
                                item
                            ) =>
                                total +
                                Number(
                                    item.netAmount ||
                                    0
                                ),

                            0
                        );

                const settled =
                    currencyCollections
                        .filter(
                            (
                                item
                            ) =>
                                item.status ===
                                "settled"
                        )
                        .reduce(
                            (
                                total,
                                item
                            ) =>
                                total +
                                Number(
                                    item.netAmount ||
                                    0
                                ),

                            0
                        );

                const gross =
                    currencyCollections
                        .filter(
                            (
                                item
                            ) =>
                                item.status !==
                                "cancelled"
                        )
                        .reduce(
                            (
                                total,
                                item
                            ) =>
                                total +
                                Number(
                                    item.grossAmount ||
                                    0
                                ),

                            0
                        );

                const commission =
                    currencyCollections
                        .filter(
                            (
                                item
                            ) =>
                                item.status !==
                                "cancelled"
                        )
                        .reduce(
                            (
                                total,
                                item
                            ) =>
                                total +
                                Number(
                                    item.commissionAmount ||
                                    0
                                ),

                            0
                        );

                return {
                    activePos:
                    currencyPosAccounts.filter(
                        (
                            item
                        ) =>
                            item.isActive !==
                            false
                    ).length,

                    pending,

                    settled,

                    gross,

                    commission,
                };
            },

            [
                currencyCollections,
                currencyPosAccounts,
            ]
        );

    /* =====================================================
       FILTER POS
    ===================================================== */

    const filteredPosAccounts =
        useMemo(
            () => {
                const query =
                    search
                        .trim()
                        .toLowerCase();

                if (!query) {
                    return posAccounts;
                }

                return posAccounts.filter(
                    (
                        pos
                    ) => {
                        const settlementAccount =
                            resolveCashAccount(
                                pos.settlementAccountId
                            );

                        return [
                            pos.name,
                            pos.bankName,
                            pos.merchantNo,
                            pos.type,
                            pos.currency,
                            settlementAccount?.name,
                            pos.description,
                        ].some(
                            (
                                value
                            ) =>
                                String(
                                    value ||
                                    ""
                                )
                                    .toLowerCase()
                                    .includes(
                                        query
                                    )
                        );
                    }
                );
            },

            [
                posAccounts,
                search,
                resolveCashAccount,
            ]
        );

    /* =====================================================
       FILTER COLLECTIONS
    ===================================================== */

    const filteredCollections =
        useMemo(
            () => {
                const query =
                    collectionSearch
                        .trim()
                        .toLowerCase();

                return collections.filter(
                    (
                        collection
                    ) => {
                        const pos =
                            resolvePos(
                                collection.posAccountId
                            );

                        const account =
                            resolveCollectionSettlementAccount(
                                collection
                            );

                        const searchMatch =
                            !query ||
                            [
                                pos?.name,
                                pos?.bankName,
                                collection.referenceNo,
                                collection.description,
                                collection.currency,
                                account?.name,
                            ].some(
                                (
                                    value
                                ) =>
                                    String(
                                        value ||
                                        ""
                                    )
                                        .toLowerCase()
                                        .includes(
                                            query
                                        )
                            );

                        const statusMatch =
                            collectionStatus ===
                            "all" ||
                            collection.status ===
                            collectionStatus;

                        return (
                            searchMatch &&
                            statusMatch
                        );
                    }
                );
            },

            [
                collections,
                collectionSearch,
                collectionStatus,
                resolvePos,
                resolveCollectionSettlementAccount,
            ]
        );

    /* =====================================================
       SETTLEMENT ACCOUNTS
    ===================================================== */

    const settlementAccounts =
        useMemo(
            () =>
                cashAccounts.filter(
                    (
                        account
                    ) =>
                        account.isActive !==
                        false &&
                        account.currency ===
                        posForm.currency
                ),

            [
                cashAccounts,
                posForm.currency,
            ]
        );

    /* =====================================================
       SELECTED COLLECTION POS
    ===================================================== */

    const selectedCollectionPos =
        useMemo(
            () =>
                posAccounts.find(
                    (
                        item
                    ) =>
                        getId(
                            item
                        ) ===
                        String(
                            collectionForm.posAccountId
                        )
                ) ||
                null,

            [
                posAccounts,
                collectionForm.posAccountId,
            ]
        );

    /* =====================================================
       COLLECTION PREVIEW

       ÖRNEK:
       Gross       = 100.000
       Commission  = %2.5
       Commission  = 2.500
       Net         = 97.500
    ===================================================== */

    const collectionPreview =
        useMemo(
            () => {
                if (
                    !selectedCollectionPos
                ) {
                    return {
                        gross:
                            0,

                        commission:
                            0,

                        net:
                            0,

                        rate:
                            0,
                    };
                }

                const gross =
                    Number(
                        collectionForm.amount ||
                        0
                    );

                const rate =
                    Number(
                        selectedCollectionPos.commissionRate ||
                        0
                    );

                const commission =
                    Number(
                        (
                            gross *
                            rate /
                            100
                        ).toFixed(
                            2
                        )
                    );

                const net =
                    Number(
                        (
                            gross -
                            commission
                        ).toFixed(
                            2
                        )
                    );

                return {
                    gross,
                    commission,
                    net,
                    rate,
                };
            },

            [
                selectedCollectionPos,
                collectionForm.amount,
            ]
        );

    /* =====================================================
       SETTLE PREVIEW
    ===================================================== */

    const settlePreview =
        useMemo(
            () => {
                if (
                    !settleTarget
                ) {
                    return null;
                }

                const pos =
                    resolvePos(
                        settleTarget.posAccountId
                    );

                const account =
                    resolveCollectionSettlementAccount(
                        settleTarget
                    );

                const currency =
                    settleTarget.currency ||
                    pos?.currency ||
                    account?.currency ||
                    "TRY";

                const gross =
                    Number(
                        settleTarget.grossAmount ||
                        0
                    );

                const commission =
                    Number(
                        settleTarget.commissionAmount ||
                        0
                    );

                const net =
                    Number(
                        settleTarget.netAmount ||
                        0
                    );

                const currentBalance =
                    Number(
                        account?.currentBalance ||
                        0
                    );

                return {
                    pos,
                    account,
                    currency,
                    gross,
                    commission,
                    net,

                    currentBalance,

                    balanceAfter:
                        currentBalance +
                        net,
                };
            },

            [
                settleTarget,
                resolvePos,
                resolveCollectionSettlementAccount,
            ]
        );

    /* =====================================================
       PENDING COLLECTION COUNT FOR POS
    ===================================================== */

    const getPendingCollectionCountForPos =
        useCallback(
            (
                pos
            ) => {
                const posId =
                    getId(
                        pos
                    );

                return collections.filter(
                    (
                        item
                    ) =>
                        getId(
                            item.posAccountId
                        ) ===
                        posId &&
                        item.status ===
                        "pending"
                ).length;
            },

            [
                collections,
            ]
        );

    /* =====================================================
       POS MODAL
    ===================================================== */

    const openAddPos =
        () => {
            setEditingPos(
                null
            );

            setPosForm(
                emptyPosForm
            );

            setPosModal(
                true
            );
        };

    const openEditPos =
        (
            pos
        ) => {
            setEditingPos(
                pos
            );

            setPosForm({
                name:
                    pos.name ||
                    "",

                bankName:
                    pos.bankName ||
                    pos.bankDetailId?.bankName ||
                    "",

                bankDetailId:
                    getId(
                        pos.bankDetailId
                    ),

                merchantNo:
                    pos.merchantNo ||
                    "",

                type:
                    pos.type ||
                    "physical",

                currency:
                    pos.currency ||
                    "TRY",

                commissionRate:
                    pos.commissionRate ??
                    "",

                settlementDays:
                    pos.settlementDays ??
                    "1",

                settlementAccountId:
                    getId(
                        pos.settlementAccountId
                    ),

                description:
                    pos.description ||
                    "",

                isActive:
                    pos.isActive !==
                    false,
            });

            setPosModal(
                true
            );
        };

    const closePosModal =
        () => {
            if (
                saving
            ) {
                return;
            }

            setPosModal(
                false
            );

            setEditingPos(
                null
            );

            setPosForm(
                emptyPosForm
            );
        };

    /* =====================================================
       BANK CHANGE
    ===================================================== */

    const handleBankChange =
        (
            bankId
        ) => {
            const bank =
                bankDetails.find(
                    (
                        item
                    ) =>
                        getId(
                            item
                        ) ===
                        String(
                            bankId
                        )
                );

            setPosForm(
                (
                    previous
                ) => ({
                    ...previous,

                    bankDetailId:
                    bankId,

                    bankName:
                        bank?.bankName ||
                        "",

                    currency:
                        editingPos &&
                        getPendingCollectionCountForPos(
                            editingPos
                        ) >
                        0
                            ? previous.currency
                            : bank?.currency ||
                            previous.currency,

                    settlementAccountId:
                        "",
                })
            );
        };

    /* =====================================================
       CURRENCY CHANGE
    ===================================================== */

    const handleCurrencyChange =
        (
            currency
        ) => {
            setPosForm(
                (
                    previous
                ) => ({
                    ...previous,

                    currency,

                    settlementAccountId:
                        "",
                })
            );
        };

    /* =====================================================
       SAVE POS
    ===================================================== */

    const savePos =
        async () => {
            try {
                /* =========================================
                   NAME
                ========================================= */

                if (
                    !normalizeText(
                        posForm.name
                    )
                ) {
                    showNotification(
                        financeText("POS name is required."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   ACCOUNT
                ========================================= */

                if (
                    !posForm.settlementAccountId
                ) {
                    showNotification(
                        financeText("Settlement account is required."),

                        "warning"
                    );

                    return;
                }

                const selectedSettlementAccount =
                    cashAccounts.find(
                        (
                            account
                        ) =>
                            getId(
                                account
                            ) ===
                            String(
                                posForm.settlementAccountId
                            )
                    );

                if (
                    !selectedSettlementAccount
                ) {
                    showNotification(
                        financeText("Settlement account could not be found."),

                        "warning"
                    );

                    return;
                }

                if (
                    selectedSettlementAccount.currency !==
                    posForm.currency
                ) {
                    showNotification(
                        financeText("POS and settlement account currencies must match."),

                        "warning"
                    );

                    return;
                }

                if (
                    selectedSettlementAccount.isActive ===
                    false
                ) {
                    showNotification(
                        financeText("Settlement account is inactive."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   COMMISSION
                ========================================= */

                const commissionRate =
                    Number(
                        posForm.commissionRate ||
                        0
                    );

                if (
                    !Number.isFinite(
                        commissionRate
                    ) ||
                    commissionRate <
                    0 ||
                    commissionRate >
                    100
                ) {
                    showNotification(
                        financeText("Commission rate must be between 0 and 100."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   SETTLEMENT DAYS
                ========================================= */

                const settlementDays =
                    Number(
                        posForm.settlementDays ||
                        0
                    );

                if (
                    !Number.isInteger(
                        settlementDays
                    ) ||
                    settlementDays <
                    0
                ) {
                    showNotification(
                        financeText("Settlement days must be zero or greater."),

                        "warning"
                    );

                    return;
                }

                /* =========================================
                   PAYLOAD
                ========================================= */

                const payload = {
                    name:
                        normalizeText(
                            posForm.name
                        ),

                    bankName:
                        normalizeText(
                            posForm.bankName
                        ),

                    bankDetailId:
                        posForm.bankDetailId ||
                        null,

                    merchantNo:
                        normalizeText(
                            posForm.merchantNo
                        ),

                    type:
                    posForm.type,

                    currency:
                    posForm.currency,

                    commissionRate,

                    settlementDays,

                    settlementAccountId:
                    posForm.settlementAccountId,

                    description:
                        normalizeText(
                            posForm.description
                        ),

                    isActive:
                    posForm.isActive,
                };

                setSaving(
                    true
                );

                if (
                    editingPos
                ) {
                    await apiRequest(
                        `/pos-accounts/${getId(
                            editingPos
                        )}`,

                        {
                            method:
                                "PATCH",

                            body:
                                JSON.stringify(
                                    payload
                                ),
                        }
                    );

                    showNotification(
                        financeText("POS account updated successfully.")
                    );
                } else {
                    await apiRequest(
                        "/pos-accounts",

                        {
                            method:
                                "POST",

                            body:
                                JSON.stringify(
                                    payload
                                ),
                        }
                    );

                    showNotification(
                        financeText("POS account created successfully.")
                    );
                }

                setPosModal(
                    false
                );

                setEditingPos(
                    null
                );

                setPosForm(
                    emptyPosForm
                );

                await fetchData();
            } catch (
                error
                ) {
                showNotification(
                    getErrorMessage(
                        error,

                        financeText("POS account could not be saved.")
                    ),

                    "error"
                );
            } finally {
                setSaving(
                    false
                );
            }
        };

    /* =====================================================
       DELETE POS
    ===================================================== */

    const openDeletePos =
        (
            pos
        ) => {
            const pendingCount =
                getPendingCollectionCountForPos(
                    pos
                );

            if (
                pendingCount >
                0
            ) {
                showNotification(
                    `${financeText("This POS account has")} ${pendingCount} ${financeText("pending settlement(s) and cannot be deleted.")}`,

                    "warning"
                );

                return;
            }

            setDeleteTarget(
                pos
            );
        };

    const confirmDeletePos =
        async () => {
            if (
                !deleteTarget
            ) {
                return;
            }

            const pendingCount =
                getPendingCollectionCountForPos(
                    deleteTarget
                );

            if (
                pendingCount >
                0
            ) {
                showNotification(
                    financeText("A POS account with pending settlements cannot be deleted."),

                    "warning"
                );

                setDeleteTarget(
                    null
                );

                return;
            }

            try {
                setSaving(
                    true
                );

                await apiRequest(
                    `/pos-accounts/${getId(
                        deleteTarget
                    )}`,

                    {
                        method:
                            "DELETE",
                    }
                );

                setDeleteTarget(
                    null
                );

                await fetchData();

                showNotification(
                    financeText("POS account deleted successfully.")
                );
            } catch (
                error
                ) {
                showNotification(
                    getErrorMessage(
                        error,

                        financeText("POS account could not be deleted.")
                    ),

                    "error"
                );
            } finally {
                setSaving(
                    false
                );
            }
        };

    /* =====================================================
       {financeText("COLLECTION")}
    ===================================================== */

    const openCollection =
        (
            pos = null
        ) => {
            if (
                pos &&
                pos.isActive ===
                false
            ) {
                showNotification(
                    financeText("Inactive POS account cannot receive new collections."),

                    "warning"
                );

                return;
            }

            setCollectionForm({
                ...emptyCollectionForm,

                posAccountId:
                    pos
                        ? getId(
                            pos
                        )
                        : "",
            });

            setCollectionModal(
                true
            );
        };

    const closeCollectionModal =
        () => {
            if (
                saving
            ) {
                return;
            }

            setCollectionModal(
                false
            );

            setCollectionForm(
                emptyCollectionForm
            );
        };

    /* =====================================================
       SAVE COLLECTION

       Bu işlem Company Cash'i değiştirmez.
    ===================================================== */

    const saveCollection =
        async () => {
            try {
                if (
                    !collectionForm.posAccountId
                ) {
                    showNotification(
                        financeText("Please select a POS account."),

                        "warning"
                    );

                    return;
                }

                if (
                    !selectedCollectionPos
                ) {
                    showNotification(
                        financeText("Selected POS account could not be found."),

                        "warning"
                    );

                    return;
                }

                if (
                    selectedCollectionPos.isActive ===
                    false
                ) {
                    showNotification(
                        financeText("Inactive POS account cannot receive new collections."),

                        "warning"
                    );

                    return;
                }

                const amount =
                    Number(
                        collectionForm.amount
                    );

                if (
                    !Number.isFinite(
                        amount
                    ) ||
                    amount <=
                    0
                ) {
                    showNotification(
                        financeText("Collection amount must be greater than zero."),

                        "warning"
                    );

                    return;
                }

                if (
                    collectionPreview.net <
                    0
                ) {
                    showNotification(
                        financeText("Net settlement amount cannot be negative."),

                        "warning"
                    );

                    return;
                }

                setSaving(
                    true
                );

                await apiRequest(
                    "/pos-accounts/collections",

                    {
                        method:
                            "POST",

                        body:
                            JSON.stringify({
                                posAccountId:
                                collectionForm.posAccountId,

                                amount,

                                customerId:
                                    collectionForm.customerId ||
                                    null,

                                referenceNo:
                                    normalizeText(
                                        collectionForm.referenceNo
                                    ),

                                description:
                                    normalizeText(
                                        collectionForm.description
                                    ),
                            }),
                    }
                );

                setCollectionModal(
                    false
                );

                setCollectionForm(
                    emptyCollectionForm
                );

                await fetchData();

                setCurrentTab(
                    1
                );

                showNotification(
                    financeText("POS collection recorded successfully. Company Cash has not changed yet.")
                );
            } catch (
                error
                ) {
                showNotification(
                    getErrorMessage(
                        error,

                        financeText("POS collection could not be created.")
                    ),

                    "error"
                );
            } finally {
                setSaving(
                    false
                );
            }
        };

    /* =====================================================
       OPEN SETTLE
    ===================================================== */

    const openSettle =
        (
            collection
        ) => {
            if (
                collection.status !==
                "pending"
            ) {
                showNotification(
                    financeText("Only pending POS collections can be settled."),

                    "warning"
                );

                return;
            }

            const account =
                resolveCollectionSettlementAccount(
                    collection
                );

            if (!account) {
                showNotification(
                    financeText("Settlement account could not be found."),

                    "error"
                );

                return;
            }

            if (
                account.isActive ===
                false
            ) {
                showNotification(
                    financeText("Settlement account is inactive."),

                    "warning"
                );

                return;
            }

            setSettleTarget(
                collection
            );
        };

    /* =====================================================
       CONFIRM SETTLE

       Backend:
       settlementAccount.currentBalance += netAmount

       CashTransaction:
       type = cash_in
       paymentMethod = pos
    ===================================================== */

    const confirmSettle =
        async () => {
            if (
                !settleTarget
            ) {
                return;
            }

            if (
                settleTarget.status !==
                "pending"
            ) {
                showNotification(
                    financeText("Collection is no longer pending."),

                    "warning"
                );

                setSettleTarget(
                    null
                );

                await fetchData();

                return;
            }

            try {
                setSaving(
                    true
                );

                await apiRequest(
                    `/pos-accounts/collections/${getId(
                        settleTarget
                    )}/settle`,

                    {
                        method:
                            "POST",
                    }
                );

                setSettleTarget(
                    null
                );

                await fetchData();

                showNotification(
                    financeText("POS collection settled successfully. Net amount was added to Company Cash.")
                );
            } catch (
                error
                ) {
                showNotification(
                    getErrorMessage(
                        error,

                        financeText("POS collection could not be settled.")
                    ),

                    "error"
                );
            } finally {
                setSaving(
                    false
                );
            }
        };

    /* =====================================================
       CANCEL COLLECTION
    ===================================================== */

    const openCancel =
        (
            collection
        ) => {
            if (
                collection.status !==
                "pending"
            ) {
                showNotification(
                    financeText("Only pending POS collections can be cancelled."),

                    "warning"
                );

                return;
            }

            setCancelTarget(
                collection
            );
        };

    const confirmCancel =
        async () => {
            if (
                !cancelTarget
            ) {
                return;
            }

            if (
                cancelTarget.status !==
                "pending"
            ) {
                showNotification(
                    financeText("Collection is no longer pending."),

                    "warning"
                );

                setCancelTarget(
                    null
                );

                await fetchData();

                return;
            }

            try {
                setSaving(
                    true
                );

                await apiRequest(
                    `/pos-accounts/collections/${getId(
                        cancelTarget
                    )}/cancel`,

                    {
                        method:
                            "POST",
                    }
                );

                setCancelTarget(
                    null
                );

                await fetchData();

                showNotification(
                    financeText("POS collection cancelled successfully.")
                );
            } catch (
                error
                ) {
                showNotification(
                    getErrorMessage(
                        error,

                        financeText("POS collection could not be cancelled.")
                    ),

                    "error"
                );
            } finally {
                setSaving(
                    false
                );
            }
        };

    /* =====================================================
       POS TABLE
    ===================================================== */

    const posTableData =
        useMemo(
            () => ({
                columns: [
                    /* =====================================
                       POS
                    ===================================== */

                    {
                        Header:
                            financeText("POS"),

                        accessor:
                            "name",

                        Cell: ({
                                   row,
                               }) => {
                            const pos =
                                row.original;

                            return (
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1.1}
                                >
                                    <MDBox
                                        width={42}
                                        height={42}
                                        minWidth={42}
                                        borderRadius="8px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            color:
                                            COLORS.blue,

                                            background:
                                            COLORS.blueLight,

                                            border:
                                                "1px solid #B2DDFF",
                                        }}
                                    >
                                        <PointOfSaleOutlined />
                                    </MDBox>

                                    <MDBox>
                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.text,
                                            }}
                                        >
                                            {pos.name ||
                                                "-"}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText(
                                                pos.type ===
                                                "virtual"
                                                    ? "Virtual POS"
                                                    : "Physical POS"
                                            )}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       BANK
                    ===================================== */

                    {
                        Header:
                            financeText("BANK"),

                        accessor:
                            "bankName",

                        Cell: ({
                                   row,
                               }) => {
                            const pos =
                                row.original;

                            return (
                                <MDBox>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.text,
                                        }}
                                    >
                                        {pos.bankName ||
                                            pos.bankDetailId?.bankName ||
                                            "-"}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {financeText("Merchant")}:{" "}
                                        {pos.merchantNo ||
                                            "-"}
                                    </MDTypography>
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       CURRENCY
                    ===================================== */

                    {
                        Header:
                            financeText("CURRENCY"),

                        accessor:
                            "currency",

                        Cell: ({
                                   value,
                               }) => (
                            <Chip
                                size="small"
                                label={
                                    value ||
                                    "-"
                                }
                                sx={{
                                    color:
                                    COLORS.blue,

                                    background:
                                    COLORS.blueLight,

                                    border:
                                        "1px solid #B2DDFF",

                                    fontWeight:
                                        800,
                                }}
                            />
                        ),
                    },

                    /* =====================================
                       COMMISSION
                    ===================================== */

                    {
                        Header:
                            financeText("COMMISSION"),

                        accessor:
                            "commissionRate",

                        Cell: ({
                                   value,
                               }) => (
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{
                                    color:
                                    COLORS.red,
                                }}
                            >
                                %
                                {Number(
                                    value ||
                                    0
                                ).toLocaleString(
                                    "tr-TR"
                                )}
                            </MDTypography>
                        ),
                    },

                    /* =====================================
                       SETTLEMENT DAYS
                    ===================================== */

                    {
                        Header:
                            financeText("SETTLEMENT"),

                        accessor:
                            "settlementDays",

                        Cell: ({
                                   value,
                               }) => (
                            <MDTypography
                                variant="button"
                                sx={{
                                    color:
                                    COLORS.text,
                                }}
                            >
                                {Number(
                                    value ||
                                    0
                                ) ===
                                0
                                    ? financeText("Same Day")
                                    : `${value} ${financeText("Day")}`}
                            </MDTypography>
                        ),
                    },

                    /* =====================================
                       SETTLEMENT ACCOUNT
                    ===================================== */

                    {
                        Header:
                            financeText("SETTLEMENT ACCOUNT"),

                        id:
                            "settlementAccount",

                        Cell: ({
                                   row,
                               }) => {
                            const pos =
                                row.original;

                            const account =
                                resolveCashAccount(
                                    pos.settlementAccountId
                                );

                            return (
                                <MDBox>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        display="block"
                                        sx={{
                                            color:
                                                account
                                                    ? COLORS.text
                                                    : COLORS.subtext,
                                        }}
                                    >
                                        {account?.name ||
                                            financeText("Not Linked")}
                                    </MDTypography>

                                    {account && (
                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {formatMoney(
                                                account.currentBalance,

                                                account.currency
                                            )}
                                        </MDTypography>
                                    )}
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       PENDING
                    ===================================== */

                    {
                        Header:
                            financeText("PENDING"),

                        id:
                            "pending",

                        Cell: ({
                                   row,
                               }) => {
                            const count =
                                getPendingCollectionCountForPos(
                                    row.original
                                );

                            return (
                                <Chip
                                    size="small"
                                    label={
                                        count
                                    }
                                    sx={{
                                        color:
                                            count >
                                            0
                                                ? COLORS.amber
                                                : COLORS.green,

                                        background:
                                            count >
                                            0
                                                ? COLORS.amberLight
                                                : COLORS.greenLight,

                                        border:
                                            count >
                                            0
                                                ? "1px solid #FEDF89"
                                                : "1px solid #ABEFC6",

                                        fontWeight:
                                            700,
                                    }}
                                />
                            );
                        },
                    },

                    /* =====================================
                       STATUS
                    ===================================== */

                    {
                        Header:
                            financeText("STATUS"),

                        accessor:
                            "isActive",

                        Cell: ({
                                   value,
                               }) => (
                            <Chip
                                size="small"
                                label={financeText(
                                    value !==
                                    false
                                        ? "Active"
                                        : "Inactive"
                                )}
                                sx={{
                                    color:
                                        value !==
                                        false
                                            ? COLORS.green
                                            : COLORS.red,

                                    background:
                                        value !==
                                        false
                                            ? COLORS.greenLight
                                            : COLORS.redLight,

                                    border:
                                        value !==
                                        false
                                            ? "1px solid #ABEFC6"
                                            : "1px solid #FECDCA",

                                    fontWeight:
                                        700,
                                }}
                            />
                        ),
                    },

                    /* =====================================
                       ACTIONS
                    ===================================== */

                    {
                        Header:
                            financeText("ACTIONS"),

                        id:
                            "actions",

                        disableSortBy:
                            true,

                        Cell: ({
                                   row,
                               }) => {
                            const pos =
                                row.original;

                            const pendingCount =
                                getPendingCollectionCountForPos(
                                    pos
                                );

                            return (
                                <MDBox
                                    display="flex"
                                    justifyContent="flex-end"
                                    gap={0.55}
                                >
                                    {/* COLLECTION */}

                                    <Tooltip
                                        title={
                                            pos.isActive ===
                                            false
                                                ? financeText("Inactive POS cannot receive collections")
                                                : financeText("Record POS collection")
                                        }
                                        arrow
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={
                                                    pos.isActive ===
                                                    false
                                                }
                                                onClick={() =>
                                                    openCollection(
                                                        pos
                                                    )
                                                }
                                                sx={getActionButtonSx(
                                                    "collection"
                                                )}
                                            >
                                                <AddOutlined fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    {/* EDIT */}

                                    <Tooltip
                                        title={financeText("Edit POS")}
                                    arrow
                                    >
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            openEditPos(
                                                pos
                                            )
                                        }
                                        sx={getActionButtonSx(
                                            "edit"
                                        )}
                                    >
                                        <EditOutlined fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                            {/* DELETE */}

                            <Tooltip
                                title={
                                    pendingCount >
                                    0
                                        ? `${pendingCount} ${financeText("pending settlement(s) must be completed or cancelled first")}`
                                        : financeText("Delete POS")
                                }
                                arrow
                            >
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={
                                                    pendingCount >
                                                    0
                                                }
                                                onClick={() =>
                                                    openDeletePos(
                                                        pos
                                                    )
                                                }
                                                sx={getActionButtonSx(
                                                    "delete"
                                                )}
                                            >
                                                <DeleteOutlineOutlined fontSize="small" />
                                            </IconButton>
                                        </span>
                            </Tooltip>
                        </MDBox>
                        );
                        },
                    },
                ],

                rows:
                filteredPosAccounts,
            }),

            [
                filteredPosAccounts,
                resolveCashAccount,
                getPendingCollectionCountForPos,
                financeLanguage,
            ]
        );

    /* =====================================================
       COLLECTION TABLE
    ===================================================== */

    const collectionTableData =
        useMemo(
            () => ({
                columns: [
                    /* =====================================
                       POS
                    ===================================== */

                    {
                        Header:
                            financeText("POS"),

                        id:
                            "pos",

                        Cell: ({
                                   row,
                               }) => {
                            const item =
                                row.original;

                            const pos =
                                resolvePos(
                                    item.posAccountId
                                );

                            return (
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <MDBox
                                        width={38}
                                        height={38}
                                        minWidth={38}
                                        borderRadius="11px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            color:
                                            COLORS.blue,

                                            background:
                                            COLORS.blueLight,
                                        }}
                                    >
                                        <CreditCardOutlined />
                                    </MDBox>

                                    <MDBox>
                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.text,
                                            }}
                                        >
                                            {pos?.name ||
                                                "-"}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {pos?.bankName ||
                                                "-"}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       DATE
                    ===================================== */

                    {
                        Header:
                            financeText("DATE"),

                        id:
                            "date",

                        Cell: ({
                                   row,
                               }) => {
                            const item =
                                row.original;

                            return (
                                <MDTypography
                                    variant="caption"
                                    sx={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {formatDate(
                                        item.collectedAt ||
                                        item.createdAt
                                    )}
                                </MDTypography>
                            );
                        },
                    },

                    /* =====================================
                       CURRENCY
                    ===================================== */

                    {
                        Header:
                            financeText("CURRENCY"),

                        id:
                            "currency",

                        Cell: ({
                                   row,
                               }) => {
                            const item =
                                row.original;

                            const pos =
                                resolvePos(
                                    item.posAccountId
                                );

                            const currency =
                                item.currency ||
                                pos?.currency ||
                                "-";

                            return (
                                <Chip
                                    size="small"
                                    label={
                                        currency
                                    }
                                    sx={{
                                        color:
                                        COLORS.blue,

                                        background:
                                        COLORS.blueLight,

                                        fontWeight:
                                            800,
                                    }}
                                />
                            );
                        },
                    },

                    /* =====================================
                       GROSS
                    ===================================== */

                    {
                        Header:
                            financeText("GROSS"),

                        accessor:
                            "grossAmount",

                        Cell: ({
                                   row,
                               }) => {
                            const item =
                                row.original;

                            const pos =
                                resolvePos(
                                    item.posAccountId
                                );

                            const currency =
                                item.currency ||
                                pos?.currency ||
                                "TRY";

                            return (
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                        COLORS.text,
                                    }}
                                >
                                    {formatMoney(
                                        item.grossAmount,

                                        currency
                                    )}
                                </MDTypography>
                            );
                        },
                    },

                    /* =====================================
                       COMMISSION
                    ===================================== */

                    {
                        Header:
                            financeText("COMMISSION"),

                        accessor:
                            "commissionAmount",

                        Cell: ({
                                   row,
                               }) => {
                            const item =
                                row.original;

                            const pos =
                                resolvePos(
                                    item.posAccountId
                                );

                            const currency =
                                item.currency ||
                                pos?.currency ||
                                "TRY";

                            return (
                                <MDBox>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.red,
                                        }}
                                    >
                                        -
                                        {formatMoney(
                                            item.commissionAmount,

                                            currency
                                        )}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        %
                                        {Number(
                                            item.commissionRate ||
                                            0
                                        ).toLocaleString(
                                            "tr-TR"
                                        )}
                                    </MDTypography>
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       NET
                    ===================================== */

                    {
                        Header:
                            financeText("NET"),

                        accessor:
                            "netAmount",

                        Cell: ({
                                   row,
                               }) => {
                            const item =
                                row.original;

                            const pos =
                                resolvePos(
                                    item.posAccountId
                                );

                            const currency =
                                item.currency ||
                                pos?.currency ||
                                "TRY";

                            return (
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                        COLORS.green,
                                    }}
                                >
                                    {formatMoney(
                                        item.netAmount,

                                        currency
                                    )}
                                </MDTypography>
                            );
                        },
                    },

                    /* =====================================
                       SETTLEMENT ACCOUNT
                    ===================================== */

                    {
                        Header:
                            financeText("SETTLEMENT ACCOUNT"),

                        id:
                            "settlementAccount",

                        Cell: ({
                                   row,
                               }) => {
                            const account =
                                resolveCollectionSettlementAccount(
                                    row.original
                                );

                            return (
                                <MDBox>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        display="block"
                                        sx={{
                                            color:
                                                account
                                                    ? COLORS.text
                                                    : COLORS.red,
                                        }}
                                    >
                                        {account?.name ||
                                            financeText("Not Found")}
                                    </MDTypography>

                                    {account && (
                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {formatMoney(
                                                account.currentBalance,

                                                account.currency
                                            )}
                                        </MDTypography>
                                    )}
                                </MDBox>
                            );
                        },
                    },

                    /* =====================================
                       REFERENCE
                    ===================================== */

                    {
                        Header:
                            financeText("REFERENCE"),

                        accessor:
                            "referenceNo",

                        Cell: ({
                                   value,
                               }) => (
                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {value ||
                                    "-"}
                            </MDTypography>
                        ),
                    },

                    /* =====================================
                       STATUS
                    ===================================== */

                    {
                        Header:
                            financeText("STATUS"),

                        accessor:
                            "status",

                        Cell: ({
                                   value,
                               }) => {
                            const config =
                                getStatusConfig(
                                    value
                                );

                            return (
                                <Chip
                                    size="small"
                                    label={financeText(
                                        config.label
                                    )}
                                    sx={{
                                        color:
                                        config.color,

                                        background:
                                        config.background,

                                        border:
                                            `1px solid ${config.border}`,

                                        fontWeight:
                                            700,
                                    }}
                                />
                            );
                        },
                    },

                    /* =====================================
                       ACTIONS
                    ===================================== */

                    {
                        Header:
                            financeText("ACTIONS"),

                        id:
                            "actions",

                        disableSortBy:
                            true,

                        Cell: ({
                                   row,
                               }) => {
                            const item =
                                row.original;

                            const pending =
                                item.status ===
                                "pending";

                            return (
                                <MDBox
                                    display="flex"
                                    justifyContent="flex-end"
                                    gap={0.55}
                                >
                                    {/* SETTLE */}

                                    <Tooltip
                                        title={
                                            pending
                                                ? financeText("Settle net amount to Company Cash")
                                                : financeText("Only pending collections can be settled")
                                        }
                                        arrow
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={
                                                    !pending
                                                }
                                                onClick={() =>
                                                    openSettle(
                                                        item
                                                    )
                                                }
                                                sx={getActionButtonSx(
                                                    "settle"
                                                )}
                                            >
                                                <SyncAltOutlined fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    {/* CANCEL */}

                                    <Tooltip
                                        title={
                                            pending
                                                ? financeText("Cancel collection")
                                                : financeText("Only pending collections can be cancelled")
                                        }
                                        arrow
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={
                                                    !pending
                                                }
                                                onClick={() =>
                                                    openCancel(
                                                        item
                                                    )
                                                }
                                                sx={getActionButtonSx(
                                                    "cancel"
                                                )}
                                            >
                                                <CancelOutlined fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </MDBox>
                            );
                        },
                    },
                ],

                rows:
                filteredCollections,
            }),

            [
                filteredCollections,
                resolvePos,
                resolveCollectionSettlementAccount,
                financeLanguage,
            ]
        );

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight:
                        "100vh",

                    background:
                    COLORS.page,

                    mx: {
                        xs:
                            -2,

                        md:
                            -3,
                    },

                    px: {
                        xs:
                            2,

                        md:
                            3,
                    },
                }}
            >
                {/* =================================================
                    HERO
                ================================================= */}

                <Card
                    sx={{
                        borderRadius:
                            "22px",

                        color:
                        COLORS.white,

                        background:
                            "linear-gradient(125deg, #081A31 0%, #0D2D52 52%, #175CD3 135%)",

                        boxShadow:
                            "0 18px 46px rgba(11,31,58,.22)",

                        overflow:
                            "hidden",

                        position:
                            "relative",

                        mb:
                            3,

                        "&::after": {
                            content:
                                '""',

                            position:
                                "absolute",

                            width:
                                360,

                            height:
                                360,

                            borderRadius:
                                "50%",

                            right:
                                -130,

                            top:
                                -180,

                            background:
                                "rgba(255,255,255,.07)",
                        },

                        "&::before": {
                            content:
                                '""',

                            position:
                                "absolute",

                            width:
                                230,

                            height:
                                230,

                            borderRadius:
                                "50%",

                            right:
                                120,

                            bottom:
                                -170,

                            background:
                                "rgba(11,165,236,.16)",
                        },
                    }}
                >
                    <MDBox
                        p={{
                            xs:
                                2.5,

                            md:
                                3.5,
                        }}
                        position="relative"
                        zIndex={1}
                    >
                        <Grid
                            container
                            spacing={3}
                            alignItems="center"
                        >
                            {/* LEFT */}

                            <Grid
                                item
                                xs={12}
                                lg={7}
                            >
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                >
                                    <MDBox
                                        width={34}
                                        height={34}
                                        borderRadius="10px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            background:
                                                "rgba(255,255,255,.12)",
                                        }}
                                    >
                                        <PointOfSaleOutlined
                                            sx={{
                                                color:
                                                    "#FFFFFF",
                                            }}
                                        />
                                    </MDBox>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                                "rgba(255,255,255,.72)",

                                            letterSpacing:
                                                ".12em",
                                        }}
                                    >
                                        {financeText("CORPORATE TREASURY")}
                                    </MDTypography>
                                </MDBox>

                                <MDTypography
                                    variant="h2"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                            "#FFFFFF",
                                    }}
                                >
                                    {financeText("POS Accounts")}
                                </MDTypography>

                                <MDTypography
                                    variant="body2"
                                    mt={1}
                                    sx={{
                                        color:
                                            "rgba(255,255,255,.72)",

                                        maxWidth:
                                            760,
                                    }}
                                >
                                    {financeText(
                                        "Manage physical and virtual POS accounts, customer card collections, commissions and settlement transfers to Company Cash."
                                    )}
                                </MDTypography>

                                <MDBox
                                    display="flex"
                                    gap={1}
                                    flexWrap="wrap"
                                    mt={2.5}
                                >
                                    <Chip
                                        icon={
                                            <StorefrontOutlined
                                                sx={{
                                                    color:
                                                        "#75E0A7 !important",
                                                }}
                                            />
                                        }
                                        label={`${posAccounts.length} ${financeText("POS accounts")}`}
                                        sx={{
                                            color:
                                                "#FFFFFF",

                                            background:
                                                "rgba(255,255,255,.10)",

                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <PaymentsOutlined
                                                sx={{
                                                    color:
                                                        "#75E0A7 !important",
                                                }}
                                            />
                                        }
                                        label={`${
                                            collections.filter(
                                                (
                                                    item
                                                ) =>
                                                    item.status ===
                                                    "pending"
                                            ).length
                                        } ${financeText("pending settlements")}`}
                                        sx={{
                                            color:
                                                "#FFFFFF",

                                            background:
                                                "rgba(255,255,255,.10)",

                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <AccountBalanceOutlined
                                                sx={{
                                                    color:
                                                        "#75E0A7 !important",
                                                }}
                                            />
                                        }
                                        label={`${cashAccounts.length} ${financeText("Company Cash accounts")}`}
                                        sx={{
                                            color:
                                                "#FFFFFF",

                                            background:
                                                "rgba(255,255,255,.10)",

                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <CalendarMonth
                                                sx={{
                                                    color:
                                                        "#B2DDFF !important",
                                                }}
                                            />
                                        }
                                        label={`${financeText("Updated")} ${lastUpdated.toLocaleTimeString(
                                            [],
                                            {
                                                hour:
                                                    "2-digit",

                                                minute:
                                                    "2-digit",
                                            }
                                        )}`}
                                        sx={{
                                            color:
                                                "#FFFFFF",

                                            background:
                                                "rgba(255,255,255,.10)",

                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />
                                </MDBox>
                            </Grid>

                            {/* RIGHT */}

                            <Grid
                                item
                                xs={12}
                                lg={5}
                            >
                                <MDBox
                                    p={2}
                                    borderRadius="16px"
                                    sx={{
                                        background:
                                            "rgba(255,255,255,.09)",

                                        border:
                                            "1px solid rgba(255,255,255,.13)",
                                    }}
                                >
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                                "rgba(255,255,255,.72)",
                                        }}
                                    >
                                        {financeText("PENDING POS SETTLEMENTS")}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        mt={0.3}
                                        sx={{
                                            color:
                                                "#B2DDFF",

                                            fontWeight:
                                                700,
                                        }}
                                    >
                                        {
                                            summaryCurrency
                                        }
                                    </MDTypography>

                                    <MDTypography
                                        variant="h3"
                                        fontWeight="bold"
                                        mt={0.7}
                                        sx={{
                                            color:
                                                "#FFFFFF",
                                        }}
                                    >
                                        {formatMoney(
                                            summary.pending,

                                            summaryCurrency
                                        )}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        mt={0.5}
                                        sx={{
                                            color:
                                                "rgba(255,255,255,.72)",
                                        }}
                                    >
                                        {financeText("Gross")}{" "}
                                        <strong>
                                            {formatMoney(
                                                summary.gross,

                                                summaryCurrency
                                            )}
                                        </strong>

                                        {" · "}

                                        {financeText("Commission")}{" "}
                                        <strong>
                                            {formatMoney(
                                                summary.commission,

                                                summaryCurrency
                                            )}
                                        </strong>
                                    </MDTypography>

                                    <MDBox
                                        display="flex"
                                        gap={1}
                                        mt={2}
                                        alignItems="stretch"
                                    >
                                        <MDBox
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                p: "4px",
                                                background: "rgba(255,255,255,.10)",
                                                border: "1px solid rgba(255,255,255,.20)",
                                                borderRadius: "12px",
                                                boxShadow: "inset 0 1px 2px rgba(0,0,0,.10)",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {[
                                                { value: "en", label: "EN", flag: "/flags/gb.svg", title: "English" },
                                                { value: "tr", label: "TR", flag: "/flags/tr.svg", title: "Türkçe" },
                                            ].map((language) => (
                                                <MDBox
                                                    key={language.value}
                                                    component="button"
                                                    type="button"
                                                    onClick={() =>
                                                        changeFinanceLanguage({
                                                            target: { value: language.value },
                                                        })
                                                    }
                                                    title={language.title}
                                                    sx={{
                                                        appearance: "none",
                                                        WebkitAppearance: "none",
                                                        border: "none",
                                                        outline: "none",
                                                        cursor: "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        gap: "7px",
                                                        height: 38,
                                                        minWidth: 76,
                                                        px: 1.15,
                                                        borderRadius: "8px",
                                                        background:
                                                            financeLanguage === language.value
                                                                ? "#FFFFFF"
                                                                : "transparent",
                                                        color:
                                                            financeLanguage === language.value
                                                                ? COLORS.navy
                                                                : "#FFFFFF",
                                                        boxShadow:
                                                            financeLanguage === language.value
                                                                ? "0 3px 10px rgba(0,0,0,.18)"
                                                                : "none",
                                                    }}
                                                >
                                                    <MDBox
                                                        component="img"
                                                        src={language.flag}
                                                        alt={language.title}
                                                        sx={{
                                                            width: 24,
                                                            height: 17,
                                                            objectFit: "cover",
                                                            borderRadius: "3px",
                                                            display: "block",
                                                        }}
                                                    />
                                                    <MDTypography
                                                        component="span"
                                                        sx={{
                                                            fontSize: "12px",
                                                            fontWeight: 800,
                                                            color: "inherit",
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        {language.label}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>

                                        <MDButton
                                            variant="contained"
                                            onClick={
                                                openAddPos
                                            }
                                            sx={{
                                                background:
                                                    "#FFFFFF !important",

                                                color:
                                                    `${COLORS.navy} !important`,

                                                flex:
                                                    1,

                                                boxShadow:
                                                    "none",
                                            }}
                                        >
                                            <AddOutlined
                                                sx={{
                                                    mr:
                                                        0.5,
                                                }}
                                            />

                                            {financeText("Add POS")}
                                        </MDButton>

                                        <Tooltip title={financeText("Refresh")}>
                                            <span style={{ display: "inline-flex" }}><IconButton
                                                onClick={
                                                    fetchData
                                                }
                                                disabled={
                                                    loading
                                                }
                                                sx={{
                                                    color:
                                                        "#FFFFFF",

                                                    border:
                                                        "1px solid rgba(255,255,255,.18)",

                                                    borderRadius:
                                                        "10px",
                                                }}
                                            >
                                                {loading ? (
                                                    <CircularProgress
                                                        size={20}
                                                        sx={{
                                                            color:
                                                                "#FFFFFF",
                                                        }}
                                                    />
                                                ) : (
                                                    <RefreshOutlined />
                                                )}
                                            </IconButton></span>
                                        </Tooltip>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                {/* =================================================
                    CURRENCY
                ================================================= */}

                <MDBox
                    display="flex"
                    justifyContent={{
                        xs:
                            "stretch",

                        sm:
                            "flex-end",
                    }}
                    mb={1.5}
                >
                    <TextField
                        select
                        size="small"
                        label={financeText("Summary Currency")}
                        value={
                            summaryCurrency
                        }
                        onChange={(
                            event
                        ) =>
                            setSummaryCurrency(
                                event.target.value
                            )
                        }
                        sx={{
                            width: {
                                xs:
                                    "100%",

                                sm:
                                    210,
                            },

                            "& .MuiOutlinedInput-root":
                                {
                                    background:
                                        "#FFFFFF",

                                    borderRadius:
                                        "11px",
                                },
                        }}
                    >
                        {CURRENCIES.map(
                            (
                                item
                            ) => (
                                <MenuItem
                                    key={
                                        item
                                    }
                                    value={
                                        item
                                    }
                                >
                                    {item}
                                </MenuItem>
                            )
                        )}
                    </TextField>
                </MDBox>

                {/* =================================================
                    METRICS
                ================================================= */}

                <Grid
                    container
                    spacing={2.5}
                >
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("Active POS")}
                            value={
                                summary.activePos
                            }
                            subtitle={`${summaryCurrency} ${financeText("POS accounts")}`}
                            icon={
                                <PointOfSaleOutlined />
                            }
                            tone="navy"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("Gross Collections")}
                            value={formatMoney(
                                summary.gross,

                                summaryCurrency
                            )}
                            subtitle={`${summaryCurrency} ${financeText("card collection volume")}`}
                            icon={
                                <CreditCardOutlined />
                            }
                            tone="blue"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("Pending Settlement")}
                            value={formatMoney(
                                summary.pending,

                                summaryCurrency
                            )}
                            subtitle={financeText("Waiting to enter Company Cash")}
                            icon={
                                <SyncAltOutlined />
                            }
                            tone="amber"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        lg={3}
                    >
                        <MetricCard
                            title={financeText("Commission")}
                            value={formatMoney(
                                summary.commission,

                                summaryCurrency
                            )}
                            subtitle={`${summaryCurrency} ${financeText("total POS commissions")}`}
                            icon={
                                <PaymentsOutlined />
                            }
                            tone="red"
                        />
                    </Grid>
                </Grid>

                {/* =================================================
                    MAIN TABLE CARD
                ================================================= */}

                <Card
                    sx={{
                        ...cardSx,

                        mt:
                            2.5,
                    }}
                >
                    <MDBox
                        px={3}
                        pt={2.5}
                    >
                        <Tabs
                            value={
                                currentTab
                            }
                            onChange={(
                                _,
                                value
                            ) =>
                                setCurrentTab(
                                    value
                                )
                            }
                            sx={{
                                borderBottom:
                                    `1px solid ${COLORS.border}`,
                            }}
                        >
                            <Tab
                                label={financeText("POS Accounts")}
                            />

                            <Tab
                                label={`${financeText("Collections")} (${collections.length})`}
                            />
                        </Tabs>
                    </MDBox>

                    {/* =================================================
                        POS TAB
                    ================================================= */}

                    {currentTab ===
                        0 && (
                            <MDBox
                                p={3}
                            >
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems={{
                                        xs:
                                            "flex-start",

                                        md:
                                            "center",
                                    }}
                                    flexDirection={{
                                        xs:
                                            "column",

                                        md:
                                            "row",
                                    }}
                                    gap={1.5}
                                    mb={2}
                                >
                                    <MDBox>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                COLORS.text,
                                            }}
                                        >
                                            {financeText("POS Accounts")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText(
                                                "Configure settlement accounts, commission rates and merchant information."
                                            )}
                                        </MDTypography>
                                    </MDBox>

                                    <MDBox
                                        display="flex"
                                        gap={1}
                                        flexWrap="wrap"
                                    >
                                        <MDButton
                                            variant="contained"
                                            onClick={() =>
                                                openCollection()
                                            }
                                            sx={{
                                                background:
                                                    `${COLORS.green} !important`,

                                                color:
                                                    "#FFFFFF !important",

                                                borderRadius:
                                                    "10px",
                                            }}
                                        >
                                            <CreditCardOutlined
                                                sx={{
                                                    mr:
                                                        0.6,
                                                }}
                                            />

                                            {financeText("New Collection")}
                                        </MDButton>

                                        <MDButton
                                            variant="contained"
                                            onClick={
                                                openAddPos
                                            }
                                            sx={{
                                                background:
                                                    `${COLORS.blue} !important`,

                                                color:
                                                    "#FFFFFF !important",

                                                borderRadius:
                                                    "10px",
                                            }}
                                        >
                                            <AddOutlined
                                                sx={{
                                                    mr:
                                                        0.6,
                                                }}
                                            />

                                            {financeText("Add POS")}
                                        </MDButton>
                                    </MDBox>
                                </MDBox>

                                <TextField
                                    fullWidth
                                    size="small"
                                    label={financeText("Search")}
                                    placeholder={financeText("POS, bank, merchant, currency or settlement account...")}
                                    value={
                                        search
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setSearch(
                                            event.target.value
                                        )
                                    }
                                    InputProps={{
                                        startAdornment:
                                            (
                                                <InputAdornment position="start">
                                                    <SearchOutlined />
                                                </InputAdornment>
                                            ),
                                    }}
                                />

                                <Divider
                                    sx={{
                                        my:
                                            2.5,
                                    }}
                                />

                                <MDBox
                                    sx={{
                                        "& .MuiTableContainer-root":
                                            {
                                                border:
                                                    `1px solid ${COLORS.border}`,

                                                borderRadius:
                                                    "10px",

                                                overflowX:
                                                    "auto",
                                            },

                                        "& table":
                                            {
                                                minWidth:
                                                    1350,
                                            },
                                    }}
                                >
                                    {loading ? (
                                        <MDBox
                                            py={8}
                                            display="flex"
                                            justifyContent="center"
                                        >
                                            <CircularProgress />
                                        </MDBox>
                                    ) : (
                                        <DataTable
                                            table={
                                                posTableData
                                            }
                                            canSearch={
                                                false
                                            }
                                            entriesPerPage={{
                                                defaultValue:
                                                    10,

                                                entries: [
                                                    5,
                                                    10,
                                                    25,
                                                    50,
                                                    100,
                                                ],
                                            }}
                                            showTotalEntries
                                            isSorted
                                            noEndBorder
                                        />
                                    )}
                                </MDBox>
                            </MDBox>
                        )}

                    {/* =================================================
                        COLLECTION TAB
                    ================================================= */}

                    {currentTab ===
                        1 && (
                            <MDBox
                                p={3}
                            >
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems={{
                                        xs:
                                            "flex-start",

                                        md:
                                            "center",
                                    }}
                                    flexDirection={{
                                        xs:
                                            "column",

                                        md:
                                            "row",
                                    }}
                                    gap={1.5}
                                    mb={2}
                                >
                                    <MDBox>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                COLORS.text,
                                            }}
                                        >
                                            {financeText("POS Collections")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText(
                                                "Pending collections do not affect Company Cash until the settlement operation is completed."
                                            )}
                                        </MDTypography>
                                    </MDBox>

                                    <MDButton
                                        variant="contained"
                                        onClick={() =>
                                            openCollection()
                                        }
                                        sx={{
                                            background:
                                                `${COLORS.green} !important`,

                                            color:
                                                "#FFFFFF !important",

                                            borderRadius:
                                                "10px",
                                        }}
                                    >
                                        <AddOutlined
                                            sx={{
                                                mr:
                                                    0.6,
                                            }}
                                        />

                                        {financeText("New Collection")}
                                    </MDButton>
                                </MDBox>

                                <Grid
                                    container
                                    spacing={2}
                                >
                                    <Grid
                                        item
                                        xs={12}
                                        lg={8}
                                    >
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={financeText("Search")}
                                            placeholder={financeText("POS, reference, settlement account...")}
                                            value={
                                                collectionSearch
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                setCollectionSearch(
                                                    event.target.value
                                                )
                                            }
                                            InputProps={{
                                                startAdornment:
                                                    (
                                                        <InputAdornment position="start">
                                                            <SearchOutlined />
                                                        </InputAdornment>
                                                    ),
                                            }}
                                        />
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                        lg={4}
                                    >
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Status")}
                                            value={
                                                collectionStatus
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                setCollectionStatus(
                                                    event.target.value
                                                )
                                            }
                                        >
                                            <MenuItem value="all">
                                                {financeText("All Statuses")}
                                            </MenuItem>

                                            <MenuItem value="pending">
                                                {financeText("Pending")}
                                            </MenuItem>

                                            <MenuItem value="settled">
                                                {financeText("Settled")}
                                            </MenuItem>

                                            <MenuItem value="cancelled">
                                                {financeText("Cancelled")}
                                            </MenuItem>
                                        </TextField>
                                    </Grid>
                                </Grid>

                                <Divider
                                    sx={{
                                        my:
                                            2.5,
                                    }}
                                />

                                <MDBox
                                    sx={{
                                        "& .MuiTableContainer-root":
                                            {
                                                border:
                                                    `1px solid ${COLORS.border}`,

                                                borderRadius:
                                                    "10px",

                                                overflowX:
                                                    "auto",
                                            },

                                        "& table":
                                            {
                                                minWidth:
                                                    1450,
                                            },
                                    }}
                                >
                                    {loading ? (
                                        <MDBox
                                            py={8}
                                            display="flex"
                                            justifyContent="center"
                                        >
                                            <CircularProgress />
                                        </MDBox>
                                    ) : (
                                        <DataTable
                                            table={
                                                collectionTableData
                                            }
                                            canSearch={
                                                false
                                            }
                                            entriesPerPage={{
                                                defaultValue:
                                                    10,

                                                entries: [
                                                    5,
                                                    10,
                                                    25,
                                                    50,
                                                    100,
                                                ],
                                            }}
                                            showTotalEntries
                                            isSorted
                                            noEndBorder
                                        />
                                    )}
                                </MDBox>
                            </MDBox>
                        )}
                </Card>
            </MDBox>

            {/* =====================================================
                ADD / EDIT POS
            ===================================================== */}

            <Dialog
                open={
                    posModal
                }
                onClose={
                    closePosModal
                }
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",
                    },
                }}
            >
                <DialogTitle>
                    <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        <MDBox
                            width={42}
                            height={42}
                            borderRadius="8px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                color:
                                    editingPos
                                        ? COLORS.purple
                                        : COLORS.blue,

                                background:
                                    editingPos
                                        ? COLORS.purpleLight
                                        : COLORS.blueLight,
                            }}
                        >
                            {editingPos ? (
                                <EditOutlined />
                            ) : (
                                <PointOfSaleOutlined />
                            )}
                        </MDBox>

                        <MDBox>
                            <MDTypography
                                variant="h6"
                                fontWeight="bold"
                            >
                                {financeText(
                                    editingPos
                                        ? "Edit POS Account"
                                        : "Add POS Account"
                                )}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText("Configure POS and Company Cash settlement information.")}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </DialogTitle>

                <DialogContent>
                    <Grid
                        container
                        spacing={2}
                        mt={0}
                    >
                        {/* NAME */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("POS Name")}
                                placeholder={financeText("Garanti Virtual POS")}
                                value={
                                    posForm.name
                                }
                                onChange={(
                                    event
                                ) =>
                                    setPosForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            name:
                                            event.target.value,
                                        })
                                    )
                                }
                            />
                        </Grid>

                        {/* BANK */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                        >
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Bank")}
                                value={
                                    posForm.bankDetailId
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleBankChange(
                                        event.target.value
                                    )
                                }
                            >
                                <MenuItem value="">
                                    {financeText("No Bank Selected")}
                                </MenuItem>

                                {bankDetails.map(
                                    (
                                        bank
                                    ) => (
                                        <MenuItem
                                            key={
                                                getId(
                                                    bank
                                                )
                                            }
                                            value={
                                                getId(
                                                    bank
                                                )
                                            }
                                        >
                                            {bank.bankName ||
                                                financeText("Bank")}

                                            {" · "}

                                            {bank.currency ||
                                                "-"}
                                        </MenuItem>
                                    )
                                )}
                            </TextField>
                        </Grid>

                        {/* MERCHANT */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Merchant No")}
                                value={
                                    posForm.merchantNo
                                }
                                onChange={(
                                    event
                                ) =>
                                    setPosForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            merchantNo:
                                            event.target.value,
                                        })
                                    )
                                }
                            />
                        </Grid>

                        {/* TYPE */}

                        <Grid
                            item
                            xs={12}
                            sm={6}
                        >
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("POS Type")}
                                value={
                                    posForm.type
                                }
                                onChange={(
                                    event
                                ) =>
                                    setPosForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            type:
                                            event.target.value,
                                        })
                                    )
                                }
                            >
                                {POS_TYPES.map(
                                    (
                                        item
                                    ) => (
                                        <MenuItem
                                            key={
                                                item.value
                                            }
                                            value={
                                                item.value
                                            }
                                        >
                                            {
                                                item.label
                                            }
                                        </MenuItem>
                                    )
                                )}
                            </TextField>
                        </Grid>

                        {/* CURRENCY */}

                        <Grid
                            item
                            xs={12}
                            sm={3}
                        >
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Currency")}
                                value={
                                    posForm.currency
                                }
                                disabled={
                                    Boolean(
                                        editingPos &&
                                        getPendingCollectionCountForPos(
                                            editingPos
                                        ) >
                                        0
                                    )
                                }
                                helperText={
                                    editingPos &&
                                    getPendingCollectionCountForPos(
                                        editingPos
                                    ) >
                                    0
                                        ? financeText("Locked while pending collections exist.")
                                        : undefined
                                }
                                onChange={(
                                    event
                                ) =>
                                    handleCurrencyChange(
                                        event.target.value
                                    )
                                }
                            >
                                {CURRENCIES.map(
                                    (
                                        item
                                    ) => (
                                        <MenuItem
                                            key={
                                                item
                                            }
                                            value={
                                                item
                                            }
                                        >
                                            {item}
                                        </MenuItem>
                                    )
                                )}
                            </TextField>
                        </Grid>

                        {/* COMMISSION */}

                        <Grid
                            item
                            xs={12}
                            sm={3}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={financeText("Commission Rate")}
                                value={
                                    posForm.commissionRate
                                }
                                inputProps={{
                                    min:
                                        0,

                                    max:
                                        100,

                                    step:
                                        "0.01",
                                }}
                                onChange={(
                                    event
                                ) =>
                                    setPosForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            commissionRate:
                                            event.target.value,
                                        })
                                    )
                                }
                                InputProps={{
                                    endAdornment:
                                        (
                                            <InputAdornment position="end">
                                                %
                                            </InputAdornment>
                                        ),
                                }}
                            />
                        </Grid>

                        {/* DAYS */}

                        <Grid
                            item
                            xs={12}
                            sm={3}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={financeText("Settlement Days")}
                                value={
                                    posForm.settlementDays
                                }
                                inputProps={{
                                    min:
                                        0,

                                    step:
                                        1,
                                }}
                                onChange={(
                                    event
                                ) =>
                                    setPosForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            settlementDays:
                                            event.target.value,
                                        })
                                    )
                                }
                            />
                        </Grid>

                        {/* STATUS */}

                        <Grid
                            item
                            xs={12}
                            sm={3}
                        >
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Status")}
                                value={
                                    posForm.isActive
                                        ? "active"
                                        : "inactive"
                                }
                                onChange={(
                                    event
                                ) =>
                                    setPosForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            isActive:
                                                event.target.value ===
                                                "active",
                                        })
                                    )
                                }
                            >
                                <MenuItem value="active">
                                    {financeText("Active")}
                                </MenuItem>

                                <MenuItem value="inactive">
                                    {financeText("Inactive")}
                                </MenuItem>
                            </TextField>
                        </Grid>

                        {/* SETTLEMENT ACCOUNT */}

                        <Grid
                            item
                            xs={12}
                        >
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Settlement Account")}
                                value={
                                    posForm.settlementAccountId
                                }
                                onChange={(
                                    event
                                ) =>
                                    setPosForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            settlementAccountId:
                                            event.target.value,
                                        })
                                    )
                                }
                                helperText={`${financeText("Only active")} ${posForm.currency} ${financeText("Company Cash accounts are shown.")}`}
                            >
                                {settlementAccounts.length ===
                                    0 && (
                                        <MenuItem
                                            value=""
                                            disabled
                                        >
                                            {financeText("No suitable Company Cash account")}
                                        </MenuItem>
                                    )}

                                {settlementAccounts.map(
                                    (
                                        account
                                    ) => (
                                        <MenuItem
                                            key={
                                                getId(
                                                    account
                                                )
                                            }
                                            value={
                                                getId(
                                                    account
                                                )
                                            }
                                        >
                                            {
                                                account.name
                                            }

                                            {" · "}

                                            {financeText(
                                                account.type ===
                                                "cash"
                                                    ? "Cash"
                                                    : "Bank"
                                            )}

                                            {" · "}

                                            {formatMoney(
                                                account.currentBalance,

                                                account.currency
                                            )}
                                        </MenuItem>
                                    )
                                )}
                            </TextField>
                        </Grid>

                        {/* DESCRIPTION */}

                        <Grid
                            item
                            xs={12}
                        >
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label={financeText("Description")}
                                value={
                                    posForm.description
                                }
                                onChange={(
                                    event
                                ) =>
                                    setPosForm(
                                        (
                                            previous
                                        ) => ({
                                            ...previous,

                                            description:
                                            event.target.value,
                                        })
                                    )
                                }
                            />
                        </Grid>

                        {/* INFO */}

                        <Grid
                            item
                            xs={12}
                        >
                            <Alert
                                severity="info"
                                sx={{
                                    borderRadius:
                                        "8px",
                                }}
                            >
                                {financeText(
                                    "The settlement account is the Company Cash account that receives the net POS amount after commission."
                                )}
                            </Alert>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions
                    sx={{
                        px:
                            3,

                        pb:
                            3,

                        gap:
                            1,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        disabled={
                            saving
                        }
                        onClick={
                            closePosModal
                        }
                    >
                        {financeText("Cancel")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        disabled={
                            saving
                        }
                        onClick={
                            savePos
                        }
                        sx={{
                            background:
                                `${COLORS.blue} !important`,

                            color:
                                "#FFFFFF !important",
                        }}
                    >
                        {saving && (
                            <CircularProgress
                                size={16}
                                sx={{
                                    color:
                                        "#FFFFFF",

                                    mr:
                                        1,
                                }}
                            />
                        )}

                        {financeText(
                            editingPos
                                ? "Update POS"
                                : "Add POS"
                        )}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* =====================================================
                NEW COLLECTION
            ===================================================== */}

            <Dialog
                open={
                    collectionModal
                }
                onClose={
                    closeCollectionModal
                }
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",
                    },
                }}
            >
                <DialogTitle>
                    <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        <MDBox
                            width={42}
                            height={42}
                            borderRadius="8px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                color:
                                COLORS.blue,

                                background:
                                COLORS.blueLight,
                            }}
                        >
                            <CreditCardOutlined />
                        </MDBox>

                        <MDBox>
                            <MDTypography
                                variant="h6"
                                fontWeight="bold"
                            >
                                {financeText("New POS Collection")}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText("Record a customer card collection.")}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </DialogTitle>

                <DialogContent>
                    <Stack
                        spacing={2}
                        mt={1}
                    >
                        {/* POS */}

                        <TextField
                            select
                            fullWidth
                            size="small"
                            label={financeText("POS Account")}
                            value={
                                collectionForm.posAccountId
                            }
                            onChange={(
                                event
                            ) =>
                                setCollectionForm(
                                    (
                                        previous
                                    ) => ({
                                        ...previous,

                                        posAccountId:
                                        event.target.value,
                                    })
                                )
                            }
                        >
                            {posAccounts
                                .filter(
                                    (
                                        pos
                                    ) =>
                                        pos.isActive !==
                                        false
                                )
                                .map(
                                    (
                                        pos
                                    ) => (
                                        <MenuItem
                                            key={
                                                getId(
                                                    pos
                                                )
                                            }
                                            value={
                                                getId(
                                                    pos
                                                )
                                            }
                                        >
                                            {pos.name}

                                            {" · "}

                                            {pos.currency}

                                            {" · "}{financeText("Commission")}{" %"}

                                            {Number(
                                                pos.commissionRate ||
                                                0
                                            )}
                                        </MenuItem>
                                    )
                                )}
                        </TextField>

                        {/* SELECTED POS INFO */}

                        {selectedCollectionPos && (
                            <MDBox
                                p={2}
                                borderRadius="8px"
                                sx={{
                                    background:
                                        "#F9FAFB",

                                    border:
                                        `1px solid ${COLORS.border}`,
                                }}
                            >
                                <Grid
                                    container
                                    spacing={1.5}
                                >
                                    <Grid
                                        item
                                        xs={12}
                                        sm={4}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            POS
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                        >
                                            {
                                                selectedCollectionPos.name
                                            }
                                        </MDTypography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                        sm={4}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText("Commission")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                COLORS.red,
                                            }}
                                        >
                                            %
                                            {Number(
                                                selectedCollectionPos.commissionRate ||
                                                0
                                            )}
                                        </MDTypography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                        sm={4}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText("Settlement")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                        >
                                            {Number(
                                                selectedCollectionPos.settlementDays ||
                                                0
                                            ) ===
                                            0
                                                ? financeText("Same Day")
                                                : `${selectedCollectionPos.settlementDays} ${financeText("Day")}`}
                                        </MDTypography>
                                    </Grid>
                                </Grid>
                            </MDBox>
                        )}

                        {/* GROSS */}

                        <TextField
                            fullWidth
                            type="number"
                            size="small"
                            label={financeText("Gross Amount")}
                            value={
                                collectionForm.amount
                            }
                            onChange={(
                                event
                            ) =>
                                setCollectionForm(
                                    (
                                        previous
                                    ) => ({
                                        ...previous,

                                        amount:
                                        event.target.value,
                                    })
                                )
                            }
                            InputProps={{
                                startAdornment:
                                    selectedCollectionPos ? (
                                        <InputAdornment position="start">
                                            {
                                                selectedCollectionPos.currency
                                            }
                                        </InputAdornment>
                                    ) : null,
                            }}
                        />

                        {/* PREVIEW */}

                        {selectedCollectionPos &&
                            collectionPreview.gross >
                            0 && (
                                <MDBox
                                    p={2}
                                    borderRadius="8px"
                                    sx={{
                                        background:
                                        COLORS.blueLight,

                                        border:
                                            "1px solid #B2DDFF",
                                    }}
                                >
                                    <Grid
                                        container
                                        spacing={1.5}
                                    >
                                        <Grid
                                            item
                                            xs={12}
                                            sm={4}
                                        >
                                            <MDTypography
                                                variant="caption"
                                                display="block"
                                                sx={{
                                                    color:
                                                    COLORS.subtext,
                                                }}
                                            >
                                                {financeText("Gross")}
                                            </MDTypography>

                                            <MDTypography
                                                variant="button"
                                                fontWeight="bold"
                                            >
                                                {formatMoney(
                                                    collectionPreview.gross,

                                                    selectedCollectionPos.currency
                                                )}
                                            </MDTypography>
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                            sm={4}
                                        >
                                            <MDTypography
                                                variant="caption"
                                                display="block"
                                                sx={{
                                                    color:
                                                    COLORS.subtext,
                                                }}
                                            >
                                                {financeText("Commission")}
                                            </MDTypography>

                                            <MDTypography
                                                variant="button"
                                                fontWeight="bold"
                                                sx={{
                                                    color:
                                                    COLORS.red,
                                                }}
                                            >
                                                -
                                                {formatMoney(
                                                    collectionPreview.commission,

                                                    selectedCollectionPos.currency
                                                )}
                                            </MDTypography>
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                            sm={4}
                                        >
                                            <MDTypography
                                                variant="caption"
                                                display="block"
                                                sx={{
                                                    color:
                                                    COLORS.subtext,
                                                }}
                                            >
                                                {financeText("Net Settlement")}
                                            </MDTypography>

                                            <MDTypography
                                                variant="button"
                                                fontWeight="bold"
                                                sx={{
                                                    color:
                                                    COLORS.green,
                                                }}
                                            >
                                                {formatMoney(
                                                    collectionPreview.net,

                                                    selectedCollectionPos.currency
                                                )}
                                            </MDTypography>
                                        </Grid>
                                    </Grid>
                                </MDBox>
                            )}

                        {/* REFERENCE */}

                        <TextField
                            fullWidth
                            size="small"
                            label={financeText("Reference No")}
                            value={
                                collectionForm.referenceNo
                            }
                            onChange={(
                                event
                            ) =>
                                setCollectionForm(
                                    (
                                        previous
                                    ) => ({
                                        ...previous,

                                        referenceNo:
                                        event.target.value,
                                    })
                                )
                            }
                        />

                        {/* DESCRIPTION */}

                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label={financeText("Description")}
                            value={
                                collectionForm.description
                            }
                            onChange={(
                                event
                            ) =>
                                setCollectionForm(
                                    (
                                        previous
                                    ) => ({
                                        ...previous,

                                        description:
                                        event.target.value,
                                    })
                                )
                            }
                        />

                        <Alert
                            severity="warning"
                            sx={{
                                borderRadius:
                                    "8px",
                            }}
                        >
                            {financeText(
                                "Recording the POS collection does not increase Company Cash. Only the net amount enters Company Cash after settlement."
                            )}
                        </Alert>
                    </Stack>
                </DialogContent>

                <DialogActions
                    sx={{
                        px:
                            3,

                        pb:
                            3,

                        gap:
                            1,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        disabled={
                            saving
                        }
                        onClick={
                            closeCollectionModal
                        }
                    >
                        {financeText("Cancel")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        disabled={
                            saving ||
                            !collectionForm.posAccountId ||
                            collectionPreview.gross <=
                            0
                        }
                        onClick={
                            saveCollection
                        }
                        sx={{
                            background:
                                `${COLORS.green} !important`,

                            color:
                                "#FFFFFF !important",
                        }}
                    >
                        {saving && (
                            <CircularProgress
                                size={16}
                                sx={{
                                    color:
                                        "#FFFFFF",

                                    mr:
                                        1,
                                }}
                            />
                        )}

                        {financeText("Record Collection")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* =====================================================
                SETTLE
            ===================================================== */}

            <Dialog
                open={Boolean(
                    settleTarget
                )}
                onClose={() => {
                    if (
                        !saving
                    ) {
                        setSettleTarget(
                            null
                        );
                    }
                }}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",
                    },
                }}
            >
                <DialogContent
                    sx={{
                        pt:
                            4,
                    }}
                >
                    <MDBox
                        width={58}
                        height={58}
                        borderRadius="50%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mx="auto"
                        mb={2}
                        sx={{
                            background:
                            COLORS.greenLight,
                        }}
                    >
                        <CheckCircleOutline
                            sx={{
                                color:
                                COLORS.green,

                                fontSize:
                                    30,
                            }}
                        />
                    </MDBox>

                    <MDTypography
                        variant="h6"
                        fontWeight="bold"
                        textAlign="center"
                    >
                        {financeText("Settle POS Collection?")}
                    </MDTypography>

                    {settlePreview && (
                        <Stack
                            spacing={2}
                            mt={2.5}
                        >
                            {/* COLLECTION */}

                            <MDBox
                                p={2}
                                borderRadius="8px"
                                sx={{
                                    background:
                                        "#F9FAFB",

                                    border:
                                        `1px solid ${COLORS.border}`,
                                }}
                            >
                                <MDTypography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    {financeText("COLLECTION")}
                                </MDTypography>

                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    display="block"
                                    mt={0.5}
                                >
                                    {settlePreview.pos?.name ||
                                        "-"}
                                </MDTypography>

                                <Grid
                                    container
                                    spacing={1.5}
                                    mt={0.3}
                                >
                                    <Grid
                                        item
                                        xs={4}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText("Gross")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                        >
                                            {formatMoney(
                                                settlePreview.gross,

                                                settlePreview.currency
                                            )}
                                        </MDTypography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={4}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText("Commission")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                COLORS.red,
                                            }}
                                        >
                                            -
                                            {formatMoney(
                                                settlePreview.commission,

                                                settlePreview.currency
                                            )}
                                        </MDTypography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={4}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText("Net")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                COLORS.green,
                                            }}
                                        >
                                            {formatMoney(
                                                settlePreview.net,

                                                settlePreview.currency
                                            )}
                                        </MDTypography>
                                    </Grid>
                                </Grid>
                            </MDBox>

                            {/* CASH ACCOUNT */}

                            <MDBox
                                p={2}
                                borderRadius="8px"
                                sx={{
                                    background:
                                    COLORS.greenLight,

                                    border:
                                        "1px solid #ABEFC6",
                                }}
                            >
                                <MDTypography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                        COLORS.green,
                                    }}
                                >
                                    {financeText("COMPANY CASH SETTLEMENT")}
                                </MDTypography>

                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    display="block"
                                    mt={0.5}
                                >
                                    {settlePreview.account?.name ||
                                        financeText("Settlement account not found")}
                                </MDTypography>

                                <Grid
                                    container
                                    spacing={1.5}
                                    mt={0.3}
                                >
                                    <Grid
                                        item
                                        xs={6}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText("Current Balance")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                        >
                                            {formatMoney(
                                                settlePreview.currentBalance,

                                                settlePreview.currency
                                            )}
                                        </MDTypography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={6}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText("Balance After Settlement")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                COLORS.green,
                                            }}
                                        >
                                            {formatMoney(
                                                settlePreview.balanceAfter,

                                                settlePreview.currency
                                            )}
                                        </MDTypography>
                                    </Grid>
                                </Grid>
                            </MDBox>

                            <Alert
                                severity="info"
                                sx={{
                                    borderRadius:
                                        "8px",
                                }}
                            >
                                {financeText(
                                    "Only the net amount will be posted to Company Cash. Gross amount and POS commission remain separately visible in POS records."
                                )}
                            </Alert>
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent:
                            "center",

                        pb:
                            3,

                        gap:
                            1,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        disabled={
                            saving
                        }
                        onClick={() =>
                            setSettleTarget(
                                null
                            )
                        }
                    >
                        {financeText("Cancel")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        disabled={
                            saving ||
                            !settlePreview?.account
                        }
                        onClick={
                            confirmSettle
                        }
                        sx={{
                            background:
                                `${COLORS.green} !important`,

                            color:
                                "#FFFFFF !important",
                        }}
                    >
                        {saving && (
                            <CircularProgress
                                size={16}
                                sx={{
                                    color:
                                        "#FFFFFF",

                                    mr:
                                        1,
                                }}
                            />
                        )}

                        {financeText("Settle Collection")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* =====================================================
                CANCEL COLLECTION
            ===================================================== */}

            <Dialog
                open={Boolean(
                    cancelTarget
                )}
                onClose={() => {
                    if (
                        !saving
                    ) {
                        setCancelTarget(
                            null
                        );
                    }
                }}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",
                    },
                }}
            >
                <DialogContent
                    sx={{
                        pt:
                            4,

                        textAlign:
                            "center",
                    }}
                >
                    <MDBox
                        width={58}
                        height={58}
                        borderRadius="50%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mx="auto"
                        mb={2}
                        sx={{
                            background:
                            COLORS.amberLight,
                        }}
                    >
                        <WarningAmberRounded
                            sx={{
                                color:
                                COLORS.amber,

                                fontSize:
                                    30,
                            }}
                        />
                    </MDBox>

                    <MDTypography
                        variant="h6"
                        fontWeight="bold"
                    >
                        {financeText("Cancel Collection?")}
                    </MDTypography>

                    <MDTypography
                        variant="body2"
                        mt={1}
                        sx={{
                            color:
                            COLORS.subtext,
                        }}
                    >
                        {financeText(
                            "This pending POS collection will be marked as cancelled. It will never increase Company Cash."
                        )}
                    </MDTypography>

                    {cancelTarget && (
                        <Chip
                            sx={{
                                mt:
                                    2,

                                color:
                                COLORS.amber,

                                background:
                                COLORS.amberLight,

                                fontWeight:
                                    700,
                            }}
                            label={formatMoney(
                                cancelTarget.netAmount,

                                cancelTarget.currency ||
                                resolvePos(
                                    cancelTarget.posAccountId
                                )?.currency ||
                                "TRY"
                            )}
                        />
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent:
                            "center",

                        pb:
                            3,

                        gap:
                            1,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        disabled={
                            saving
                        }
                        onClick={() =>
                            setCancelTarget(
                                null
                            )
                        }
                    >
                        {financeText("Back")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        disabled={
                            saving
                        }
                        onClick={
                            confirmCancel
                        }
                        sx={{
                            background:
                                `${COLORS.amber} !important`,

                            color:
                                "#FFFFFF !important",
                        }}
                    >
                        {saving && (
                            <CircularProgress
                                size={16}
                                sx={{
                                    color:
                                        "#FFFFFF",

                                    mr:
                                        1,
                                }}
                            />
                        )}

                        {financeText("Cancel Collection")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* =====================================================
                DELETE POS
            ===================================================== */}

            <Dialog
                open={Boolean(
                    deleteTarget
                )}
                onClose={() => {
                    if (
                        !saving
                    ) {
                        setDeleteTarget(
                            null
                        );
                    }
                }}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",
                    },
                }}
            >
                <DialogContent
                    sx={{
                        pt:
                            4,

                        textAlign:
                            "center",
                    }}
                >
                    <MDBox
                        width={58}
                        height={58}
                        borderRadius="50%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mx="auto"
                        mb={2}
                        sx={{
                            background:
                            COLORS.redLight,
                        }}
                    >
                        <DeleteOutlineOutlined
                            sx={{
                                color:
                                COLORS.red,

                                fontSize:
                                    30,
                            }}
                        />
                    </MDBox>

                    <MDTypography
                        variant="h6"
                        fontWeight="bold"
                    >
                        {financeText("Delete POS Account?")}
                    </MDTypography>

                    <MDTypography
                        variant="body2"
                        mt={1}
                        sx={{
                            color:
                            COLORS.subtext,
                        }}
                    >
                        <strong>
                            {
                                deleteTarget?.name
                            }
                        </strong>{" "}
                        {financeText("will be permanently deleted.")}
                    </MDTypography>

                    <Alert
                        severity="warning"
                        sx={{
                            mt:
                                2,

                            textAlign:
                                "left",

                            borderRadius:
                                "8px",
                        }}
                    >
                        {financeText("A POS account with pending settlements cannot be deleted.")}
                    </Alert>
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent:
                            "center",

                        pb:
                            3,

                        gap:
                            1,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        disabled={
                            saving
                        }
                        onClick={() =>
                            setDeleteTarget(
                                null
                            )
                        }
                    >
                        {financeText("Close")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        disabled={
                            saving
                        }
                        onClick={
                            confirmDeletePos
                        }
                        sx={{
                            background:
                                `${COLORS.red} !important`,

                            color:
                                "#FFFFFF !important",
                        }}
                    >
                        {saving && (
                            <CircularProgress
                                size={16}
                                sx={{
                                    color:
                                        "#FFFFFF",

                                    mr:
                                        1,
                                }}
                            />
                        )}

                        {financeText("Delete POS")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* =====================================================
                SNACKBAR
            ===================================================== */}

            <Snackbar
                open={
                    notification.open
                }
                autoHideDuration={
                    4500
                }
                onClose={
                    closeNotification
                }
                anchorOrigin={{
                    vertical:
                        "bottom",

                    horizontal:
                        "right",
                }}
            >
                <Alert
                    severity={
                        notification.severity
                    }
                    variant="filled"
                    onClose={
                        closeNotification
                    }
                    sx={{
                        width:
                            "100%",

                        borderRadius:
                            "8px",
                    }}
                >
                    {
                        notification.message
                    }
                </Alert>
            </Snackbar>

            <Footer />
        </DashboardLayout>
    );
};

export default PosAccounts;